import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { HelpHint } from "@/components/HelpHint";
import { Plus, Trash2, Pencil, Users } from "lucide-react";
import { toast } from "sonner";
import { todayISO } from "./helpers";

const CAT_OPTS = [["cash_allowance", "Cash allowance"], ["employer_benefit", "Employer-funded benefit"], ["additional_leave", "Additional leave"], ["display_only", "Display only"]];
const VAL_OPTS = [["fixed_annual", "Fixed annual (£/yr)"], ["fixed_monthly", "Fixed monthly (£/mo)"], ["actual_annual", "Actual annual (£/yr)"], ["percent_salary", "% of salary"], ["pension_percent", "Pension (qualifying earnings %)"], ["calculated_leave", "Calculated leave"], ["manual", "Manual"], ["display_only", "Display only"]];

export default function BenefitCatalogueManager() {
  const [rows, setRows] = useState(null);
  const [dlg, setDlg] = useState(null);
  const [bulk, setBulk] = useState(false);
  const load = () => api.get("/reward/catalogue?include_retired=true").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);
  const retire = async (id) => {
    if (!window.confirm("Retire this benefit? It stays on existing statements but can't be newly assigned.")) return;
    try { await api.post(`/reward/catalogue/${id}/retire`); toast.success("Benefit retired"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const save = async (f, id) => {
    try {
      const body = { name: f.name, category: f.category, valuation_method: f.valuation_method,
        default_value: f.default_value === "" ? null : Number(f.default_value), provider: f.provider || null,
        description: f.description || null, taxable: f.taxable, employee_visible: f.employee_visible, include_in_total: f.include_in_total };
      if (id) await api.put(`/reward/catalogue/${id}`, body); else await api.post("/reward/catalogue", body);
      toast.success("Benefit saved"); setDlg(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  if (rows === null) return <div className="text-slate-400 text-sm" data-testid="catalogue-loading">Loading catalogue…</div>;
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm space-y-4" data-testid="benefit-catalogue-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div><h3 className="font-semibold text-[#0B1A2F]">Benefit catalogue</h3><p className="text-xs text-slate-400">The library of benefits, allowances and additional leave that can be assigned to employees.</p></div>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setBulk(true)} data-testid="bulk-assign-open-btn"><Users size={14} className="mr-1" />Bulk assign</Button>
          <Button size="sm" variant="outline" onClick={() => setDlg({ data: { name: "", category: "employer_benefit", valuation_method: "fixed_annual", default_value: "", provider: "", description: "", taxable: false, employee_visible: true, include_in_total: true } })} data-testid="catalogue-add-btn"><Plus size={14} className="mr-1" />Add benefit</Button>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50"><tr>{["Name", "Category", "Valuation", "Default", "Taxable", "Status", ""].map((h) => <th key={h} className="text-left px-3 py-2 text-xs uppercase text-slate-600">{h}</th>)}</tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className={`border-t border-slate-50 ${c.retired ? "opacity-50" : ""}`} data-testid={`catalogue-row-${c.id}`}>
                <td className="px-3 py-2 font-medium text-[#0B1A2F]">{c.name}</td>
                <td className="px-3 py-2 text-slate-500">{(CAT_OPTS.find((x) => x[0] === c.category) || [])[1] || c.category}</td>
                <td className="px-3 py-2 text-slate-500">{(VAL_OPTS.find((x) => x[0] === c.valuation_method) || [])[1] || c.valuation_method}</td>
                <td className="px-3 py-2 tabular">{c.default_value != null ? (c.valuation_method === "percent_salary" ? `${c.default_value}%` : `£${Number(c.default_value).toLocaleString()}`) : "—"}</td>
                <td className="px-3 py-2">{c.taxable ? "Yes" : "No"}</td>
                <td className="px-3 py-2">{c.retired ? <span className="text-xs text-rose-500">Retired</span> : <span className="text-xs text-emerald-600">Active</span>}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" className="h-7" onClick={() => setDlg({ id: c.id, data: { name: c.name, category: c.category, valuation_method: c.valuation_method, default_value: c.default_value ?? "", provider: c.provider || "", description: c.description || "", taxable: !!c.taxable, employee_visible: c.employee_visible !== false, include_in_total: c.include_in_total !== false } })} data-testid={`catalogue-edit-${c.id}`}><Pencil size={13} /></Button>
                  {!c.retired && <Button size="sm" variant="ghost" className="h-7 text-rose-500" onClick={() => retire(c.id)} data-testid={`catalogue-retire-${c.id}`}><Trash2 size={13} /></Button>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {dlg && <CatalogueDialog dlg={dlg} onClose={() => setDlg(null)} onSave={save} />}
      {bulk && <BulkAssignDialog rows={(rows || []).filter((c) => !c.retired)} onClose={() => setBulk(false)} />}
    </div>
  );
}

function CatalogueDialog({ dlg, onClose, onSave }) {
  const [f, setF] = useState(dlg.data);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="catalogue-dialog">
        <DialogHeader><DialogTitle>{dlg.id ? "Edit benefit" : "Add benefit"}</DialogTitle><DialogDescription>Define how this benefit is categorised and valued on Total Reward statements.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-slate-500">Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="catalogue-name-input" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Category</Label>
              <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger data-testid="catalogue-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{CAT_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
            <div><Label className="text-xs text-slate-500 flex items-center gap-1">Valuation <HelpHint text="How this benefit's value is calculated for Total Reward: a fixed £ amount per year, or a percentage of the employee's salary. Enter the amount/percent in 'Default value'." testid="catalogue-valuation-hint" /></Label>
              <Select value={f.valuation_method} onValueChange={(v) => setF({ ...f, valuation_method: v })}><SelectTrigger data-testid="catalogue-valuation-select"><SelectValue /></SelectTrigger>
                <SelectContent>{VAL_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent></Select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Default value (£ or %)</Label><Input type="number" value={f.default_value} onChange={(e) => setF({ ...f, default_value: e.target.value })} data-testid="catalogue-default-input" /></div>
            <div><Label className="text-xs text-slate-500">Provider</Label><Input value={f.provider} onChange={(e) => setF({ ...f, provider: e.target.value })} data-testid="catalogue-provider-input" /></div>
          </div>
          <div><Label className="text-xs text-slate-500">Description</Label><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="catalogue-desc-input" /></div>
          <div className="flex flex-wrap gap-4 pt-1">
            <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={f.taxable} onCheckedChange={(v) => setF({ ...f, taxable: v })} data-testid="catalogue-taxable" />Taxable</label>
            <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={f.employee_visible} onCheckedChange={(v) => setF({ ...f, employee_visible: v })} data-testid="catalogue-visible" />Visible to employee</label>
            <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={f.include_in_total} onCheckedChange={(v) => setF({ ...f, include_in_total: v })} data-testid="catalogue-include" />Include in total</label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" disabled={!f.name} onClick={() => onSave(f, dlg.id)} data-testid="catalogue-save-btn">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkAssignDialog({ rows, onClose }) {
  const [emps, setEmps] = useState([]);
  const [catalogueId, setCatalogueId] = useState(rows[0]?.id || "");
  const [scope, setScope] = useState("all");
  const [scopeValue, setScopeValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [includeInTotal, setIncludeInTotal] = useState(true);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/employees").then((r) => setEmps(r.data || [])).catch(() => setEmps([])); }, []);

  const today = todayISO();
  const active = emps.filter((e) => !(e.end_date && e.end_date < today));
  const departments = [...new Set(active.map((e) => e.department).filter(Boolean))].sort();
  const managerIds = new Set(active.map((e) => e.manager_id).filter(Boolean));
  const managers = active.filter((e) => managerIds.has(e.id)).sort((a, b) => `${a.first_name}`.localeCompare(b.first_name));

  const targetCount = scope === "all" ? active.length
    : scope === "department" ? active.filter((e) => e.department === scopeValue).length
    : scope === "manager" ? active.filter((e) => e.manager_id === scopeValue).length : 0;

  const submit = async () => {
    if (!catalogueId) { toast.error("Choose a benefit"); return; }
    if (scope !== "all" && !scopeValue) { toast.error("Choose a target for this scope"); return; }
    setBusy(true);
    try {
      const r = await api.post("/reward/benefits/bulk-assign", {
        catalogue_id: catalogueId, scope, scope_value: scope === "all" ? null : scopeValue,
        start_date: startDate || null, include_in_total: includeInTotal, admin_note: note || null, skip_existing: true });
      toast.success(`Assigned "${r.data.benefit}" to ${r.data.assigned} employee${r.data.assigned !== 1 ? "s" : ""}${r.data.skipped ? ` · ${r.data.skipped} already had it` : ""}.`);
      onClose();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="bulk-assign-dialog">
        <DialogHeader><DialogTitle>Bulk assign a benefit</DialogTitle>
          <DialogDescription>Assign one catalogue benefit to a group of employees at once. Anyone who already has it is skipped.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs text-slate-500">Benefit</Label>
            <Select value={catalogueId} onValueChange={setCatalogueId}>
              <SelectTrigger data-testid="bulk-benefit-select"><SelectValue placeholder="Choose a benefit" /></SelectTrigger>
              <SelectContent>{rows.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs text-slate-500">Assign to</Label>
              <Select value={scope} onValueChange={(v) => { setScope(v); setScopeValue(""); }}>
                <SelectTrigger data-testid="bulk-scope-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All active staff</SelectItem>
                  <SelectItem value="department">A department</SelectItem>
                  <SelectItem value="manager">A manager's team</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {scope === "department" && (
              <div><Label className="text-xs text-slate-500">Department</Label>
                <Select value={scopeValue} onValueChange={setScopeValue}>
                  <SelectTrigger data-testid="bulk-department-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{departments.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            {scope === "manager" && (
              <div><Label className="text-xs text-slate-500">Manager</Label>
                <Select value={scopeValue} onValueChange={setScopeValue}>
                  <SelectTrigger data-testid="bulk-manager-select"><SelectValue placeholder="Choose…" /></SelectTrigger>
                  <SelectContent>{managers.map((m) => <SelectItem key={m.id} value={m.id}>{m.first_name} {m.last_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div><Label className="text-xs text-slate-500">Start date (optional)</Label><Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="bulk-start-date" /></div>
          <label className="flex items-center gap-2 text-sm text-slate-600">Include in Total Reward value <HelpHint text="When on, this benefit's value is added to each employee's indicative Total Reward total. Turn off for perks you don't want counted." testid="bulk-include-hint" /><Switch checked={includeInTotal} onCheckedChange={setIncludeInTotal} data-testid="bulk-include-total" /></label>
          <div><Label className="text-xs text-slate-500">Admin note (optional)</Label><Textarea rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></div>
          <p className="text-xs text-slate-500 bg-slate-50 rounded-md px-3 py-2" data-testid="bulk-target-count">This will assign the benefit to <strong>{targetCount}</strong> active employee{targetCount !== 1 ? "s" : ""} (those who already have it are skipped).</p>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button className="bg-[#2EA3FD] hover:bg-[#1B8EE6]" onClick={submit} disabled={busy} data-testid="bulk-assign-submit">{busy ? "Assigning…" : "Assign benefit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
